import React from 'react';
import PropTypes from 'prop-types';
import { FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import useThemeStyles from '../../hooks/useThemeStyles';

/**
 * Reusable Dropdown component
 * @param {string} label - Label for the dropdown
 * @param {string} value - Current selected value
 * @param {function} onChange - Callback when value changes
 * @param {Array} options - Array of option objects with {value, label, icon?}
 * @param {boolean} fullWidth - Whether dropdown should take full width
 * @param {boolean} disabled - Whether dropdown is disabled
 * @param {object} sx - Additional Material-UI sx styles
 * @param {string} id - Optional ID for the select element
 * @param {string} name - Optional name attribute
 */
const Dropdown = ({
  label,
  value,
  onChange,
  options = [],
  fullWidth = true,
  disabled = false,
  sx = {},
  id,
  name,
  error = false,
  helperText,
}) => {
  const { colors } = useThemeStyles();

  return (
    <FormControl fullWidth={fullWidth} error={error} sx={sx}>
      <InputLabel
        id={id ? `${id}-label` : undefined}
        sx={{
          color: colors.text.secondary,
          '&.Mui-focused': {
            color: colors.interactive.primary,
          },
        }}
      >
        {label}
      </InputLabel>
      <Select
        id={id}
        name={name}
        value={value}
        label={label}
        onChange={onChange}
        disabled={disabled}
        sx={{
          '& .MuiSelect-select': {
            color: colors.text.primary,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border.subtle,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.interactive.primary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.interactive.primary,
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.icon && `${option.icon} `}
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, ml: 1.5 }}
        >
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

Dropdown.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
    })
  ),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  sx: PropTypes.shape({}),
  id: PropTypes.string,
  name: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
};

Dropdown.defaultProps = {
  options: [],
  fullWidth: true,
  disabled: false,
  sx: {},
  id: undefined,
  name: undefined,
  error: false,
  helperText: undefined,
};

export default Dropdown;
